// ============================================================================
// Feuille du noyau : ÉTUDIER UNE FONCTION — le tableau de variations (refonte, lot 6.20,
// moteur v42 étendu — docs/moteur/REFONTE_MOTEUR.md §5.40).
// ============================================================================
// Un QCM sur les variations d'une fonction (« f′(x) = (x − 1)(x + 2). f est décroissante
// sur… », « f(x) = x eˣ. Minimum de f ? ») : chaque choix est une AFFIRMATION sur f
// (`kernel.vclaims`, dans l'ordre des choix), tranchée par le tableau de variations.
//
//   1. Dériver f (si l'énoncé ne donne pas f′) : les lignes « f′(x) = … » de la feuille
//      d'analyse (lot 5.9), jugées par son juge ; factoriser f′ pour en lire le signe.
//   2. Le tableau de variations : UN bout « ⊞{…} » (format du tableau de signes, lot 6.19,
//      avec une ligne de plus, celle de f) — les facteurs de f′, la ligne de f′, puis les
//      flèches de f et, sous chaque valeur où f′ s'annule, la valeur de f (si l'énoncé
//      donne f). Vérifié case par case ; juste : 3 HORA (`variations.table@1`).
//   3. Le tableau tranche les affirmations (comme la feuille d'affirmations, lot 6.8) :
//      « vraie » / « fausse » en tête de feuille, choix barrés au QCM ; avec `single`, une
//      affirmation qui ne se lit pas dans le tableau est fausse par élimination.
//
// Les vérités sont EXACTES : signes du tableau attendu (racines exactes), valeurs de f par
// substitution exacte. Pur : aucune dépendance à l'app.
// ============================================================================

import { normalize, type Chip } from './lexer';
import type { KernelVariationClaim, ScratchModel } from './model';
import { parseLine, type Line } from './tree';
import {
  caretAtoms,
  createKAnaState,
  kanaCommit,
  kanaEstablished,
  kanaHolds,
  kanaIsAnswer,
  kanaJudge,
  kanaLine,
  kanaLineAtoms,
  kanaNextMove,
  kanaText,
  type KAnaState,
  type KAnaVerdict,
} from './kernel-analysis';
import {
  checkSignTable,
  expectedSignTable,
  isSignTableText,
  parseSignTable,
  sameSignValue,
  signTableCandidates as signCandidates,
  signTableText,
  type ExpectedSignTable,
} from './kernel-signs';
import type { SignTableSkeleton } from './kernel-sheet';
import { evalFloat } from './kernel/normal';
import {
  canon,
  fromNode,
  parseStatement,
  relationsIn,
  substitute,
  termKey,
  type Rel,
  type Term,
} from './kernel/term';

/* ------------------------------ pas ------------------------------ */

export const VARIATIONS_STEP_IDS = { table: 'variations.table@1' } as const;

const TABLE_RULE = {
  id: VARIATIONS_STEP_IDS.table,
  card: 'Variations',
  label: 'Tableau de variations',
  hora: 3,
  why: 'Le signe de f′ donne le sens de variation de f : là où f′ est positive, f croît (↗) ; là où elle est négative, f décroît (↘). Sous chaque valeur où f′ s’annule, la valeur de f.',
};

/* ------------------------------ modèle ------------------------------ */

export interface KVarVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step'
    | 'dup';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  /** la case fausse du tableau (ligne -1 : la ligne des x ; la ligne de f : après f′) */
  cell?: { row: number; col?: number };
  /** affirmations que le tableau tranche (indices, avec leur vérité) */
  decided?: { index: number; truth: boolean }[];
  /** la ligne est le tableau de variations juste */
  vtable?: true;
  /** ligne de la dérivée (verdict du juge d'analyse) */
  amove?: KAnaVerdict['amove'];
}

/** un morceau d'intervalle (bornes en valeur approchée, exactes à l'écriture près) */
interface Piece {
  lo: number;
  hi: number;
  loC: boolean;
  hiC: boolean;
}

interface VClaim {
  spec: KernelVariationClaim;
  /** vérité exacte (null : ne se lit pas dans le tableau) */
  truth: boolean | null;
}

/** le tableau de variations attendu */
export interface ExpectedVarTable {
  /** le tableau de signes de f′ (dernière ligne : « f′(x) ») */
  sign: ExpectedSignTable;
  /** flèche de chaque intervalle */
  arrows: ('↗' | '↘')[];
  /** sous chaque valeur de x : valeur interdite ? */
  forbidden: boolean[];
  /** sous chaque valeur de x : la valeur exacte de f (null : interdite ou f inconnue) */
  values: (Term | null)[];
  /** valeurs de x (approchées, pour comparer) */
  floats: number[];
  /** signe de f′ sur chaque intervalle */
  signs: ('+' | '−')[];
}

export interface KVarState {
  model: ScratchModel;
  /** la lettre de la fonction (« f ») et la variable (« x ») */
  f: string;
  x: string;
  /** l'expression de f ; null si l'énoncé ne donne que f′ */
  fExpr: Term | null;
  /** l'écriture de f′ qui sert au tableau (donnée, ou la dernière écrite sans nom) */
  fprime: Term | null;
  /** f′ donnée par l'énoncé */
  given: boolean;
  /** dériver f (l'énoncé ne donne pas f′) : l'état de la feuille d'analyse */
  ana: KAnaState | null;
  /** le tableau juste est sur la feuille */
  table: ExpectedVarTable | null;
  claims: VClaim[];
  /** décision de chaque affirmation (après le tableau) */
  decisions: (boolean | null)[];
  single: boolean;
  path: string[];
  lines: number;
  /** textes des lignes validées */
  texts: string[];
  /** cache : tableau attendu par écriture de f′ */
  cache: Map<string, ExpectedVarTable | null>;
}

export class VariationsModelError extends Error {}

/* ------------------------------ lecture ------------------------------ */

const HEAD_RE = /^\s*([A-Za-z])(′*)\(\s*([a-zα-ω])\s*\)\s*$/;

/** « f(x) = … » / « f′(x) = … » → { base, primes, x, expr } ; null sinon */
function readDefinition(
  text: string,
): { base: string; primes: number; x: string; expr: Term } | null {
  const i = text.indexOf('=');
  if (i < 0 || text.indexOf('=', i + 1) >= 0) return null;
  const m = HEAD_RE.exec(text.slice(0, i).replace(/'/g, '′'));
  if (!m) return null;
  let expr: Term;
  try {
    const line = parseLine(normalize(caretAtoms(kanaLineAtoms(text))), { chains: true });
    const d = line.dis[0];
    if (line.dis.length !== 1 || !d || d.sides.length !== 2) return null;
    expr = canon(fromNode(d.sides[1]!));
  } catch {
    return null;
  }
  return { base: m[1]!, primes: m[2]!.length, x: m[3]!, expr };
}

/** un nombre écrit (« −1 ÷ exp(1) », « 0,5 ») ; null s'il ne se lit pas */
function readNumber(text: string): Term | null {
  const p = parseStatement(text);
  if (!p.ok || p.s.k !== 'expr') return null;
  return Number.isFinite(evalFloat(p.s.e, {})) ? p.s.e : null;
}

const flip: Readonly<Record<Rel, Rel>> = { '<': '>', '>': '<', '≤': '≥', '≥': '≤', '=': '=' };

/** « −2 ≤ x ≤ 1 », « x ≤ 3 », « x > −3 » → un morceau ; null sinon */
export function pieceOf(text: string, x: string): Piece | null {
  const p = parseStatement(text);
  if (!p.ok) return null;
  if (p.s.k === 'or' || p.s.k === 'expr' || (p.s.k === 'set' && p.s.clauses.length !== 1))
    return null;
  const piece: Piece = { lo: -Infinity, hi: Infinity, loC: false, hiC: false };
  for (const r of relationsIn(p.s)) {
    const xl = r.l.k === 'sym' && r.l.n === x;
    const xr = r.r.k === 'sym' && r.r.n === x;
    if (xl === xr) return null;
    const c = evalFloat(xl ? r.r : r.l, {});
    if (!Number.isFinite(c)) return null;
    const rel = xl ? r.rel : flip[r.rel];
    if (rel === '=') return null;
    if (rel === '<' || rel === '≤') {
      piece.hi = c;
      piece.hiC = rel === '≤';
    } else {
      piece.lo = c;
      piece.loC = rel === '≥';
    }
  }
  return piece.lo < piece.hi ? piece : null;
}

const WHOLE: Piece = { lo: -Infinity, hi: Infinity, loC: false, hiC: false };
const EPS = 1e-9;

/* ------------------------------ tableau attendu ------------------------------ */

function fAt(ks: Pick<KVarState, 'fExpr' | 'x'>, c: Term): Term | null {
  if (!ks.fExpr) return null;
  const v = canon(substitute(ks.fExpr, { [ks.x]: c }));
  return Number.isFinite(evalFloat(v, {})) ? v : null;
}

/** le tableau de variations attendu pour cette écriture de f′ ; null si son signe ne se lit pas */
export function expectedVarTable(
  ks: Pick<KVarState, 'f' | 'x' | 'fExpr' | 'cache'>,
  fp: Term,
): ExpectedVarTable | null {
  const key = termKey(canon(fp));
  if (ks.cache.has(key)) return ks.cache.get(key)!;
  let out: ExpectedVarTable | null = null;
  const sign = expectedSignTable(fp, ks.x);
  if (sign) {
    const rows = sign.rows.map((r) => ({ ...r, cells: [...r.cells] }));
    rows[rows.length - 1]!.label = `${ks.f}′(${ks.x})`;
    const last = rows[rows.length - 1]!.cells;
    const n = sign.values.length;
    const signs: ('+' | '−')[] = [];
    const arrows: ('↗' | '↘')[] = [];
    let ok = true;
    for (let i = 0; i <= n; i++) {
      const s = last[2 * i];
      if (s !== '+' && s !== '−') ok = false;
      signs.push(s === '+' ? '+' : '−');
      arrows.push(s === '+' ? '↗' : '↘');
    }
    const forbidden = sign.values.map((_, i) => last[2 * i + 1] === '‖');
    const values = sign.values.map((v, i) => (forbidden[i] ? null : fAt(ks, v)));
    if (ok)
      out = {
        sign: { ...sign, rows, subject: `${ks.f}′(${ks.x})` },
        arrows,
        forbidden,
        values,
        floats: sign.values.map((v) => evalFloat(v, {})),
        signs,
      };
  }
  ks.cache.set(key, out);
  return out;
}

/** le tableau juste, écrit comme une ligne de la feuille (corrigé, tests) */
export function expectedVarTableText(
  exp: ExpectedVarTable,
  valueTexts: (string | null)[] = [],
): string {
  const n = exp.sign.values.length;
  const cells: string[] = [];
  for (let i = 0; i <= n; i++) {
    cells.push(exp.arrows[i]!);
    if (i < n) cells.push(exp.forbidden[i] ? '‖' : (valueTexts[i] ?? ''));
  }
  return signTableText({
    values: exp.sign.valueTexts,
    rows: exp.sign.rows.map((r) => ({ label: r.label, cells: r.cells })),
    f: { label: exp.sign.subject.replace('′', ''), cells },
  });
}

/* ------------------------------ vérités ------------------------------ */

/** indices des intervalles du tableau qui rencontrent l'intérieur du morceau */
function overlapping(t: ExpectedVarTable, p: Piece): number[] {
  const out: number[] = [];
  const n = t.floats.length;
  for (let i = 0; i <= n; i++) {
    const lo = i === 0 ? -Infinity : t.floats[i - 1]!;
    const hi = i === n ? Infinity : t.floats[i]!;
    if (Math.max(lo, p.lo) < Math.min(hi, p.hi) - EPS) out.push(i);
  }
  return out;
}

const inPiece = (c: number, p: Piece): boolean =>
  (c > p.lo + EPS || (p.loC && Math.abs(c - p.lo) <= EPS)) &&
  (c < p.hi - EPS || (p.hiC && Math.abs(c - p.hi) <= EPS));

/** une valeur interdite dans le morceau */
function forbiddenIn(t: ExpectedVarTable, p: Piece): boolean {
  return t.floats.some((c, i) => t.forbidden[i] && inPiece(c, p));
}

function monotone(t: ExpectedVarTable, p: Piece, dir: '+' | '−'): boolean {
  const ov = overlapping(t, p);
  return !!ov.length && !forbiddenIn(t, p) && ov.every((i) => t.signs[i] === dir);
}

/** l'indice de la valeur c dans le tableau ; −1 si c n'y est pas */
function valueIndex(t: ExpectedVarTable, c: Term): number {
  return t.sign.values.findIndex((v) => sameSignValue(v, c));
}

/** f a-t-elle en c un extremum global (sur le morceau D) ? */
function globalAt(t: ExpectedVarTable, c: number, D: Piece, kind: 'min' | 'max'): boolean {
  if (!inPiece(c, D) || forbiddenIn(t, D)) return false;
  const left: Piece = { lo: D.lo, loC: D.loC, hi: c, hiC: false };
  const right: Piece = { lo: c, loC: false, hi: D.hi, hiC: D.hiC };
  const down = kind === 'min' ? '−' : '+';
  const up = kind === 'min' ? '+' : '−';
  const okL = left.hi - left.lo <= EPS || monotone(t, left, down);
  const okR = right.hi - right.lo <= EPS || monotone(t, right, up);
  return okL && okR;
}

/** f a-t-elle en la k-ième valeur un extremum local ? */
function localAt(t: ExpectedVarTable, k: number, kind: 'min' | 'max'): boolean {
  if (k < 0 || t.forbidden[k]) return false;
  const [a, b] = [t.signs[k], t.signs[k + 1]];
  return kind === 'min' ? a === '−' && b === '+' : a === '+' && b === '−';
}

/** les points où un extremum global (sur D) peut être atteint : valeurs du tableau, bornes */
function globalPoints(t: ExpectedVarTable, D: Piece): { c: number; term: Term | null }[] {
  const out: { c: number; term: Term | null }[] = t.sign.values.map((v, i) => ({
    c: t.floats[i]!,
    term: v,
  }));
  if (D.loC && Number.isFinite(D.lo)) out.push({ c: D.lo, term: null });
  if (D.hiC && Number.isFinite(D.hi)) out.push({ c: D.hi, term: null });
  return out;
}

export interface ClaimTruthContext {
  x: string;
  fExpr: Term | null;
}

/**
 * La vérité exacte d'une affirmation, lue dans le tableau attendu ; null si elle ne s'y
 * lit pas (`other`). Lève `VariationsModelError` si l'affirmation est mal formée.
 */
export function claimTruth(
  ctx: ClaimTruthContext,
  t: ExpectedVarTable,
  c: KernelVariationClaim,
): boolean | null {
  const x = ctx.x;
  const piece = (s: string): Piece => {
    const p = pieceOf(s, x);
    if (!p) throw new VariationsModelError(`intervalle illisible : « ${s} »`);
    return p;
  };
  const pieces = (c.on ?? []).map(piece);
  const D = pieces[0] ?? WHOLE;
  const at = (c.at ?? []).map((s) => {
    const v = readNumber(s);
    if (!v) throw new VariationsModelError(`abscisse illisible : « ${s} »`);
    return v;
  });
  const valueOk = (where: Term | null, v: Term): boolean => {
    if (!ctx.fExpr) throw new VariationsModelError('une valeur de f demande f(x) dans `start`');
    if (!where) return false;
    const fv = canon(substitute(ctx.fExpr, { [x]: where }));
    return sameSignValue(fv, v);
  };
  const value = c.value ? readNumber(c.value) : null;
  if (c.value && !value) throw new VariationsModelError(`valeur illisible : « ${c.value} »`);
  switch (c.kind) {
    case 'other':
      return null;
    case 'constant':
      return false;
    case 'increasing':
    case 'decreasing': {
      const dir = c.kind === 'increasing' ? '+' : '−';
      return (pieces.length ? pieces : [WHOLE]).every((p) => monotone(t, p, dir));
    }
    case 'sign': {
      const ov = overlapping(t, D);
      if (c.sign === '0') return false;
      if (c.sign === 'change')
        return ov.some((i) => t.signs[i] === '+') && ov.some((i) => t.signs[i] === '−');
      return !!ov.length && ov.every((i) => t.signs[i] === c.sign);
    }
    case 'sequence': {
      const runs: ('increasing' | 'decreasing')[] = [];
      for (const i of overlapping(t, D)) {
        const r = t.signs[i] === '+' ? 'increasing' : 'decreasing';
        if (runs[runs.length - 1] !== r) runs.push(r);
      }
      return !forbiddenIn(t, D) && runs.join() === (c.seq ?? []).join();
    }
    case 'extrema': {
      const pts = t.sign.values.filter((_, k) => localAt(t, k, 'min') || localAt(t, k, 'max'));
      return pts.length === at.length && at.every((a) => pts.some((p) => sameSignValue(p, a)));
    }
    case 'no_extremum': {
      const k = at[0] ? valueIndex(t, at[0]) : -1;
      if (k < 0 || t.forbidden[k]) return false;
      return (
        t.sign.rows[t.sign.rows.length - 1]!.cells[2 * k + 1] === '0' &&
        t.signs[k] === t.signs[k + 1]
      );
    }
    case 'no_min':
    case 'no_max': {
      const kind = c.kind === 'no_min' ? 'min' : 'max';
      return !globalPoints(t, D).some((p) => globalAt(t, p.c, D, kind));
    }
    case 'min':
    case 'max': {
      const kind = c.kind;
      if (at[0]) {
        const where = at[0];
        const cf = evalFloat(where, {});
        const k = valueIndex(t, where);
        const ok = c.local ? localAt(t, k, kind) && inPiece(cf, D) : globalAt(t, cf, D, kind);
        if (!ok) return false;
        return value ? valueOk(where, value) : true;
      }
      if (!value) throw new VariationsModelError('un extremum sans abscisse ni valeur');
      if (c.local) {
        return t.sign.values.some(
          (v, k) => localAt(t, k, kind) && inPiece(t.floats[k]!, D) && valueOk(v, value),
        );
      }
      const best = globalPoints(t, D).find((p) => globalAt(t, p.c, D, kind));
      if (!best) return false;
      if (!best.term)
        throw new VariationsModelError(
          'extremum à une borne : sa valeur n’est pas dans le tableau',
        );
      return valueOk(best.term, value);
    }
  }
}

/* ------------------------------ état ------------------------------ */

export function createKVarState(model: ScratchModel): KVarState {
  const spec = model.kernel!;
  if (spec.plugin !== 'variations' || !spec.vclaims)
    throw new VariationsModelError('Étude de fonction attendue (plugin « variations »).');
  const start = readDefinition(spec.start);
  if (!start || start.primes > 1)
    throw new VariationsModelError('Départ attendu : « f(x) = … » ou « f′(x) = … ».');
  const { base: f, x } = start;
  const fExpr = start.primes === 0 ? start.expr : null;
  // f′ donnée par l'énoncé (départ « f′(x) = … », ou une donnée)
  let fprime: Term | null = start.primes === 1 ? start.expr : null;
  for (const g of model.givens ?? []) {
    const d = readDefinition(g);
    if (d && d.base === f && d.x === x && d.primes === 1) fprime = d.expr;
  }
  const given = !!fprime;
  const path = spec.path ?? [];
  if (!given && !fExpr) throw new VariationsModelError('f′ inconnue : donne f(x) ou f′(x).');
  // dériver f : la partie « analyse » du corrigé (avant le tableau)
  const amodel = given ? null : analysisModelOf(model);
  if (!given && !amodel)
    throw new VariationsModelError('Le corrigé doit dériver f (« f′(x) = … »).');
  const ana: KAnaState | null = amodel ? createKAnaState(amodel) : null;
  const ks: KVarState = {
    model,
    f,
    x,
    fExpr,
    fprime,
    given,
    ana,
    table: null,
    claims: [],
    decisions: spec.vclaims.map(() => null),
    single: !!spec.single,
    path,
    lines: 0,
    texts: [],
    cache: new Map(),
  };
  // les vérités, lues dans le tableau du corrigé (dernière écriture de f′ du corrigé)
  const ref = referenceTable(ks);
  ks.claims = spec.vclaims.map((c) => ({ spec: c, truth: ref ? claimTruth(ks, ref, c) : null }));
  return ks;
}

/**
 * Dériver f : la feuille d'analyse (lot 5.9) que la feuille contient — départ « f(x) = … »,
 * cible f′(x), corrigé = les lignes du corrigé avant le tableau ; null si l'énoncé donne f′.
 */
export function analysisModelOf(model: ScratchModel): ScratchModel | null {
  const spec = model.kernel;
  if (spec?.plugin !== 'variations') return null;
  const start = readDefinition(spec.start);
  if (!start || start.primes !== 0) return null;
  const { base: f, x } = start;
  const givesPrime = (model.givens ?? []).some((g) => {
    const d = readDefinition(g);
    return !!d && d.base === f && d.primes === 1;
  });
  if (givesPrime) return null;
  const dpath = (spec.path ?? []).filter((t) => !isSignTableText(t));
  const last = [...dpath].reverse().find((t) => {
    const d = readDefinition(t);
    return !!d && d.base === f && d.primes === 1;
  });
  if (!last) return null;
  return {
    ...model,
    traps: [],
    kernel: {
      plugin: 'analysis',
      goal: { kind: 'differentiate', target: `${f}′(${x})` },
      start: spec.start,
      answer: last,
      path: dpath,
      ...(spec.domain ? { domain: spec.domain } : {}),
    },
  } as ScratchModel;
}

/** le tableau attendu pour la dernière écriture de f′ du corrigé (ou celle de l'énoncé) */
function referenceTable(ks: KVarState): ExpectedVarTable | null {
  let fp = ks.fprime;
  for (const t of ks.path) {
    const d = readDefinition(t);
    if (d && d.base === ks.f && d.primes === 1) fp = d.expr;
  }
  return fp ? expectedVarTable(ks, fp) : null;
}

/* ------------------------------ jugement ------------------------------ */

function decisionsAfterTable(ks: KVarState): (boolean | null)[] {
  const d = ks.claims.map((c) => c.truth);
  if (ks.single) {
    const trues = d.filter((v) => v === true).length;
    if (trues === 1) return d.map((v) => v === true);
    const open = d.filter((v) => v === null).length;
    if (!trues && open === 1) return d.map((v) => v === null);
  }
  return d;
}

const intervalText = (exp: ExpectedVarTable, i: number): string => {
  const v = exp.sign.valueTexts;
  const lo = i === 0 ? '−∞' : v[i - 1]!;
  const hi = i === v.length ? '+∞' : v[i]!;
  return `]${lo} ; ${hi}[`;
};

const norm = (s: string): string => s.replace(/\s+/g, '').replace(/-/g, '−').replace(/'/g, '′');

function judgeTable(ks: KVarState, text: string): KVarVerdict {
  const A = [text];
  const t = parseSignTable(text);
  if (!t)
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Tableau illisible',
      message: 'Je ne lis pas ce tableau : ouvre-le de nouveau depuis la palette.',
      badChip: 0,
    };
  if (!ks.fprime)
    return {
      kind: 'unjustified',
      atoms: A,
      title: `Calcule d’abord ${ks.f}′(${ks.x})`,
      message: `Le sens de variation de ${ks.f} se lit sur le signe de ${ks.f}′ : dérive ${ks.f}, puis dresse le tableau.`,
    };
  const exp = expectedVarTable(ks, ks.fprime);
  if (!exp)
    return {
      kind: 'unjustified',
      atoms: A,
      title: `Factorise ${ks.f}′(${ks.x})`,
      message: `Pour lire le signe de ${ks.f}′(${ks.x}), écris-la comme un produit (ou un quotient) de facteurs dont tu connais le signe.`,
    };
  if (ks.table && text === lastTableText(ks))
    return {
      kind: 'dup',
      atoms: A,
      title: 'Déjà dressé',
      message: 'Ce tableau est déjà sur ta feuille : lis-le pour trancher les choix.',
    };
  const issue = checkSignTable(exp.sign, { values: t.values, rows: t.rows });
  if (issue)
    return {
      kind: 'false',
      atoms: A,
      title: issue.title,
      message: issue.message,
      badChip: 0,
      ...(issue.row !== undefined
        ? { cell: { row: issue.row, ...(issue.col !== undefined ? { col: issue.col } : {}) } }
        : {}),
    };
  const fRow = t.rows.length;
  if (!t.f)
    return {
      kind: 'false',
      atoms: A,
      title: `Il manque la ligne de ${ks.f}`,
      message: `Sous la ligne de ${ks.f}′(${ks.x}), écris les flèches de ${ks.f} : ↗ là où ${ks.f}′ est positive, ↘ là où elle est négative.`,
      badChip: 0,
      cell: { row: fRow },
    };
  if (norm(t.f.label) !== norm(`${ks.f}(${ks.x})`) && norm(t.f.label) !== norm(ks.f))
    return {
      kind: 'false',
      atoms: A,
      title: 'La dernière ligne',
      message: `La dernière ligne du tableau est celle de ${ks.f} : ouvre le tableau depuis la palette.`,
      badChip: 0,
    };
  const n = exp.sign.values.length;
  for (let c = 0; c < 2 * n + 1; c++) {
    const have = t.f.cells[c] ?? '';
    const i = c >> 1;
    const cell = { row: fRow, col: c };
    if (c % 2 === 0) {
      const want = exp.arrows[i]!;
      if (have === want) continue;
      if (!have)
        return {
          kind: 'false',
          atoms: A,
          title: 'Il manque une flèche',
          message: `Mets une flèche sur chaque intervalle : il en manque une sur ${intervalText(exp, i)}.`,
          badChip: 0,
          cell,
        };
      return {
        kind: 'false',
        atoms: A,
        title: 'Pas le bon sens de variation',
        message: `Sur ${intervalText(exp, i)}, la flèche suit le signe de ${ks.f}′ : positive, ${ks.f} croît (↗) ; négative, ${ks.f} décroît (↘).`,
        badChip: 0,
        cell,
      };
    }
    const vx = exp.sign.valueTexts[i]!;
    if (exp.forbidden[i]) {
      if (have === '‖') continue;
      return {
        kind: 'false',
        atoms: A,
        title: 'Une valeur interdite',
        message: `${ks.f} n’est pas définie en ${ks.x} = ${vx} : mets une double barre ‖ sous cette valeur.`,
        badChip: 0,
        cell,
      };
    }
    if (have === '‖')
      return {
        kind: 'false',
        atoms: A,
        title: 'La double barre',
        message: `${ks.f} est définie en ${ks.x} = ${vx} : la double barre ne va que sous une valeur interdite.`,
        badChip: 0,
        cell,
      };
    const want = exp.values[i];
    if (!want) {
      if (!have) continue;
      return {
        kind: 'false',
        atoms: A,
        title: `On ne connaît pas ${ks.f}`,
        message: `L’énoncé ne donne que ${ks.f}′ : laisse vide la case sous ${ks.x} = ${vx}.`,
        badChip: 0,
        cell,
      };
    }
    if (!have)
      return {
        kind: 'false',
        atoms: A,
        title: `La valeur de ${ks.f}`,
        message: `Sous ${ks.x} = ${vx}, écris ${ks.f}(${vx}) : remplace ${ks.x} par ${vx} dans ${ks.f}(${ks.x}).`,
        badChip: 0,
        cell,
      };
    const got = readNumber(have);
    if (!got || !sameSignValue(got, want))
      return {
        kind: 'false',
        atoms: A,
        title: 'Pas la bonne valeur',
        message: `${ks.f}(${vx}) ne vaut pas ${have} : remplace ${ks.x} par ${vx} dans ${ks.f}(${ks.x}) et calcule.`,
        badChip: 0,
        cell,
      };
  }
  const decisions = decisionsAfterTable(ks);
  const decided = decisions.flatMap((d, index) => (d === null ? [] : [{ index, truth: d }]));
  return {
    kind: 'step',
    atoms: A,
    hora: TABLE_RULE.hora,
    rule: TABLE_RULE,
    decided,
    final: decisions.every((d) => d !== null),
    vtable: true,
  };
}

function lastTableText(ks: KVarState): string | null {
  for (let i = ks.texts.length - 1; i >= 0; i--)
    if (isSignTableText(ks.texts[i]!)) return ks.texts[i]!;
  return null;
}

export function kvarJudge(ks: KVarState, chips: Chip[], opts?: { verify?: boolean }): KVarVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A };
  const tables = A.filter(isSignTableText);
  if (tables.length) {
    if (A.length !== 1)
      return {
        kind: 'syntax',
        atoms: A,
        title: 'Le tableau seul sur sa ligne',
        message: 'Un tableau occupe toute une ligne : efface ce qu’il y a autour.',
        badChip: A.findIndex((a) => !isSignTableText(a)),
      };
    return judgeTable(ks, A[0]!);
  }
  if (!ks.ana)
    return {
      kind: 'unjustified',
      atoms: A,
      complete: true,
      title: 'Dresse le tableau de variations',
      message: `${ks.f}′(${ks.x}) est donnée : étudie son signe dans le tableau de variations (bouton « Tableau » de la palette).`,
    };
  const v = kanaJudge(ks.ana, chips, opts) as unknown as KVarVerdict;
  // f′ écrite : la feuille continue avec le tableau
  if (v.kind === 'step' && v.final)
    return {
      ...v,
      final: false,
      ...(v.rule && {
        rule: {
          ...v.rule,
          why: v.rule.why.replace(
            ' C’est la réponse.',
            ` Étudie maintenant son signe : dresse le tableau de variations de ${ks.f}.`,
          ),
        },
      }),
    };
  return v;
}

export function kvarCommit(ks: KVarState, v: KVarVerdict): void {
  const text = v.vtable ? v.atoms[0]! : kanaText(v.atoms);
  if (v.vtable) {
    ks.table = expectedVarTable(ks, ks.fprime!);
    ks.decisions = decisionsAfterTable(ks);
  } else if (ks.ana) {
    kanaCommit(ks.ana, v as unknown as KAnaVerdict);
    // une écriture de f′ sans nom : celle qui sert au tableau
    const m = v.amove;
    if (m?.kind === 'target' && kanaIsAnswer(ks.ana, text))
      ks.fprime = m.members[m.members.length - 1]!;
  }
  ks.texts.push(text);
  ks.lines++;
}

export function kvarDecisions(ks: KVarState): (boolean | null)[] {
  return ks.decisions;
}

export function kvarGoalReached(ks: KVarState): boolean {
  return !!ks.table && ks.decisions.every((d) => d !== null);
}

/** la ligne est vraie (une ligne de dérivée juste ; le tableau juste) */
export function kvarHolds(ks: KVarState, text: string): boolean {
  if (isSignTableText(text)) {
    const v = judgeTable({ ...ks, table: null, texts: [] }, text);
    return v.kind === 'step';
  }
  return !!ks.ana && kanaHolds(ks.ana, text);
}

/** une ligne déjà sur la feuille */
export function kvarEstablished(ks: KVarState, text: string): boolean {
  if (isSignTableText(text)) return !!ks.table && ks.texts.includes(text);
  return !!ks.ana && kanaEstablished(ks.ana, text);
}

export function kvarLineAtoms(text: string): string[] {
  return isSignTableText(text) ? [text] : kanaLineAtoms(text);
}

/** coup de pouce : dériver (la ligne suivante du corrigé), puis le tableau */
export function kvarNextMove(
  ks: KVarState,
): { line: Line; text: string; atoms: string[]; label: string; table?: true } | null {
  if (kvarGoalReached(ks)) return null;
  if (ks.ana && !ks.fprime) {
    const m = kanaNextMove(ks.ana);
    if (m) return m;
  }
  const exp = ks.fprime ? expectedVarTable(ks, ks.fprime) : null;
  if (ks.fprime && !exp && ks.ana) {
    // f′ écrite mais pas factorisée : la suite du corrigé
    const m = kanaNextMove(ks.ana);
    if (m) return m;
    for (const t of ks.path) {
      if (isSignTableText(t) || kanaEstablished(ks.ana, t)) continue;
      const atoms = kanaLineAtoms(t);
      const v = kanaJudge(
        ks.ana,
        atoms.map((a) => ({ atoms: [a] })),
        { verify: true },
      );
      if (v.kind === 'step')
        return { line: kanaLine(t), text: kanaText(atoms), atoms, label: v.rule?.label ?? '' };
    }
  }
  if (!ks.fprime) return null;
  const table = ks.path.find(isSignTableText) ?? '';
  return {
    line: { dis: [] } as unknown as Line,
    text: table,
    atoms: [table],
    label: TABLE_RULE.label,
    table: true,
  };
}

/** le corrigé rejoué (porte, plafond des HORA) */
export function kvarSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KVarVerdict }[];
  reached: boolean;
} {
  const ks = createKVarState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KVarVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = kvarLineAtoms(t);
    const v = kvarJudge(
      ks,
      atoms.map((a) => ({ atoms: [a] })),
      { verify: true },
    );
    if (v.kind !== 'step') break;
    kvarCommit(ks, v);
    lines.push({
      line: isSignTableText(t) ? ({ dis: [] } as unknown as Line) : kanaLine(t),
      text: isSignTableText(t) ? t : kanaText(atoms),
      atoms,
      v,
    });
  }
  return { lines, reached: kvarGoalReached(ks) };
}

/* ------------------------------ éditeur, palette ------------------------------ */

export type VarTableSkeleton = SignTableSkeleton & {
  /** la ligne de f : son étiquette, des valeurs à écrire (si f est connue), les valeurs proposées */
  variation: { label: string; values: boolean; candidates: string[] };
};

/** les valeurs écrites dans les tableaux du corrigé (sous chaque valeur de x) */
function pathValueTexts(ks: KVarState): string[] {
  const out: string[] = [];
  for (const t of ks.path) {
    const tab = isSignTableText(t) ? parseSignTable(t) : null;
    tab?.f?.cells.forEach((c, i) => {
      if (i % 2 === 1 && c && c !== '‖' && !out.includes(c)) out.push(c);
    });
  }
  return out;
}

/** ce que l'éditeur propose (lignes, valeurs de x, valeurs de f) ; null avant f′ */
export function kvarTableSkeleton(ks: KVarState): VarTableSkeleton | null {
  if (!ks.fprime) return null;
  const exp = expectedVarTable(ks, ks.fprime);
  if (!exp) return null;
  // valeurs de x : celles du tableau de signes (racines et nombres écrits dans f′)
  const xs = new Set<string>(exp.sign.valueTexts);
  const sign = expectedSignTable(ks.fprime, ks.x);
  if (sign) for (const c of signCandidates(ks.fprime, sign)) xs.add(c);
  // valeurs de f : celles du corrigé, puis celles des affirmations (les pièges du QCM)
  const fvals: { text: string; t: Term }[] = [];
  const push = (s: string): void => {
    const t = readNumber(s);
    if (!t || fvals.some((o) => sameSignValue(o.t, t))) return;
    fvals.push({ text: s, t });
  };
  pathValueTexts(ks).forEach(push);
  ks.claims.forEach((c) => c.spec.value && push(c.spec.value));
  return {
    x: ks.x,
    subject: `${ks.f}(${ks.x})`,
    labels: exp.sign.rows.map((r) => r.label),
    candidates: [...xs].sort((a, b) => a.localeCompare(b, 'fr')),
    done: !!ks.table,
    variation: {
      label: `${ks.f}(${ks.x})`,
      values: exp.values.some((v) => v !== null),
      candidates: fvals.map((o) => o.text).sort((a, b) => a.localeCompare(b, 'fr')),
    },
  };
}

/** textes des affirmations, dans l'ordre des choix */
export function kvarClaimTexts(model: ScratchModel): string[] {
  return (model.kernel?.vclaims ?? []).map((c) => c.text);
}
